import { css, html, LitElement, nothing, TemplateResult, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import mainStyles from './styles/main.scss?inline';
import { updateLocale } from './localization/config';
import './components/image-gallery.js';
import './components/gallery-overlay.js';
import type { GalleryImage } from './types/gallery.types.js';
import type { ImageRenderData } from './utils/gallery.utils.js';
import {
    buildAllGalleryRenderData,
    preloadAspectRatios,
    sortGalleryImagesByAspectRatio,
} from './utils/gallery.utils.js';
import { AnalyticsService } from './services/analytics.service.js';
import { fetchRoundtripGalleryContent } from './services/roundtrip-gallery-content.service.js';
import { galleryGridTemplate } from './templates/gallery-grid.template.js';
import { galleryFullscreenTemplate } from './templates/gallery-fullscreen.template.js';
import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel';
import Fade from 'embla-carousel-fade';
import { interceptChildEvents, setInitialUri, triggerSchemaJsonEvent } from './services/schema-org-json-ld.service';

type ContentState = 'loading' | 'loaded' | 'empty';

/**
 * Main entry component for the roundtrip gallery.
 * It renders the teaser gallery and controls the overlay state.
 */
@localized()
export class RoundtripGallery extends LitElement {
    static shadowRootOptions = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    };

    static override styles = [
        css`
            ${unsafeCSS(mainStyles)}
        `,
    ];

    @property({ type: String })
    public lang = '';

    @property({ type: String })
    public currency = '';

    @property({ type: String })
    public brand = '';

    @property({ type: String })
    public tenant = '';

    @property({ type: String, attribute: 'identifier' })
    public identifier = '';

    @property({ type: String, attribute: 'travelcontext' })
    public travelContext = 'ROUNDTRIPS';

    @property({ type: Object, attribute: 'additional-tracking-data' })
    public additionalTrackingData: Record<string, unknown> = {};

    @property({ type: Array })
    public images?: GalleryImage[];

    @property({ type: String, attribute: 'facet_audience' })
    public facetAudience = '';

    @property({ type: String, attribute: 'facet_style' })
    public facetStyle = '';

    @property({ type: String, attribute: 'facet_motive' })
    public facetMotive = '';

    /**
     * Optional separate images source for the overlay grid.
     * Falls back to `images` when not provided.
     */
    @property({ type: Array, attribute: 'overlay-images' })
    public overlayImages?: GalleryImage[];

    @state()
    private _preloadedImages: GalleryImage[] = [];

    @state()
    private _preloadedOverlayImages: GalleryImage[] = [];

    @state()
    private _isPreparingProvidedImages = false;

    /**
     * True once the middleware fetch path has settled (success, empty response,
     * error, or "missing required params"). Only relevant when no `images` prop
     * is provided. The provided-images path is tracked via
     * `_isPreparingProvidedImages` / `_preloadedImages` instead.
     */
    @state()
    private _middlewareResolved = false;

    private _removeSchemaEventInterceptor: (() => void) | null = null;

    /**
     * Returns the effective teaser images.
     * @returns GalleryImage array for the teaser view
     */
    private get _effectiveImages(): GalleryImage[] {
        if (this.images?.length) {
            if (this._isPreparingProvidedImages && !this._preloadedImages.length) {
                return [];
            }

            const source = this._preloadedImages.length ? this._preloadedImages : this.images;
            return sortGalleryImagesByAspectRatio(source, {
                sortMode: 'landscape',
            });
        }

        if (this._middlewareImages.length) {
            return sortGalleryImagesByAspectRatio(this._middlewareImages, {
                sortMode: 'landscape',
            });
        }

        return [];
    }

    /**
     * Returns the effective overlay images.
     * @returns GalleryImage array for the overlay grid
     */
    private get _effectiveOverlayImages(): GalleryImage[] {
        if (this.overlayImages?.length) {
            const source = this._preloadedOverlayImages.length ? this._preloadedOverlayImages : this.overlayImages;
            return sortGalleryImagesByAspectRatio(source, {
                sortMode: 'landscape',
            });
        }

        if (this._middlewareImages.length) {
            return sortGalleryImagesByAspectRatio(this._middlewareImages, {
                sortMode: 'landscape',
            });
        }

        return this._effectiveImages;
    }

    /**
     * Derives the graceful-degradation state for the outer placeholder from the
     * existing loading flags, without introducing extra state-writes throughout
     * the two independent loading paths (provided images vs. middleware fetch).
     * @returns Current ContentState for the gallery placeholder
     */
    private get _skeletonState(): ContentState {
        const usesProvidedImages = Boolean(this.images?.length);

        if (usesProvidedImages) {
            if (this._isPreparingProvidedImages && !this._preloadedImages.length) {
                return 'loading';
            }
            return this._effectiveImages.length ? 'loaded' : 'empty';
        }

        if (!this._middlewareResolved) {
            return 'loading';
        }

        return this._effectiveImages.length ? 'loaded' : 'empty';
    }

    @state()
    private _overlayOpen = false;

    @state()
    private _middlewareImages: GalleryImage[] = [];

    @state()
    private _overlayImages: ImageRenderData[] = [];

    @state()
    private _activeIndex: number | null = null;

    @state()
    private _currentSlideIndex = 0;

    private _visibilityObserver: IntersectionObserver | null = null;

    private _visibilityTimerId: number | null = null;

    private _hasVisibilityEventFired = false;

    private _lastMiddlewareRequestKey: string | null = null;

    private _preloadRunId = 0;

    private _emblaInstance: EmblaCarouselType | null = null;

    private _fullscreenAnalyticsSent = false;

    /**
     * Viewports that skip the image grid and open fullscreen directly (when 6+ images).
     * Narrow width matches CSS `max-width: 767px`; short height covers phones in landscape
     * where `innerWidth` is often ≥768 (e.g. Pixel 9).
     * @returns Whether the current window dimensions match the compact gallery breakpoint
     */
    private _isCompactGalleryViewport(): boolean {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.innerWidth < 768 || window.innerHeight < 560;
    }

    /**
     * Desktop viewports use the Embla fade plugin; mobile and tablet use horizontal slide.
     * Matches the DESKTOP image breakpoint (1280px). Short height excludes phone landscape.
     * @returns Whether the fullscreen carousel should use the fade transition
     */
    private _shouldUseEmblaFade(): boolean {
        if (typeof window === 'undefined') {
            return false;
        }
        return window.innerWidth >= 1280 && window.innerHeight >= 560;
    }

    /**
     * Initializes localization when the component is attached to the DOM.
     * @returns Promise that resolves after locale resources are loaded
     */
    async connectedCallback(): Promise<void> {
        super.connectedCallback();

        this._setupVisibilityTracking();

        this._removeSchemaEventInterceptor?.();
        this._removeSchemaEventInterceptor = interceptChildEvents(this);
        const langAttribute = this.lang ?? 'de-CH';
        await updateLocale(langAttribute);
        await this._loadGalleryFromMiddleware();
        void this._preloadProvidedImageRatios();
    }

    /**
     * Remove event listeners when the component is disconnected.
     */
    override disconnectedCallback(): void {
        this._cleanupVisibilityTracking();
        this._destroyEmbla();
        super.disconnectedCallback();
    }

    private _setupVisibilityTracking(): void {
        if (typeof window === 'undefined' || this._hasVisibilityEventFired || !('IntersectionObserver' in window)) {
            return;
        }

        this._visibilityObserver = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry) return;

                if (entry.intersectionRatio >= 0.5) {
                    this._startVisibilityTimer();
                    return;
                }

                this._clearVisibilityTimer();
            },
            {
                threshold: 0.5,
            }
        );

        this._visibilityObserver.observe(this);
    }

    private _cleanupVisibilityTracking(): void {
        this._clearVisibilityTimer();
        this._visibilityObserver?.disconnect();
        this._visibilityObserver = null;
    }

    private _startVisibilityTimer(): void {
        if (typeof window === 'undefined' || this._hasVisibilityEventFired || this._visibilityTimerId !== null) {
            return;
        }

        this._visibilityTimerId = window.setTimeout(() => {
            this._visibilityTimerId = null;

            if (this._hasVisibilityEventFired) return;

            this._hasVisibilityEventFired = true;
            this._visibilityObserver?.disconnect();
            this._visibilityObserver = null;
            this._dispatchVisibilityAnalyticsEvent();
        }, 500);
    }

    private _clearVisibilityTimer(): void {
        if (typeof window === 'undefined' || this._visibilityTimerId === null) {
            return;
        }

        window.clearTimeout(this._visibilityTimerId);
        this._visibilityTimerId = null;
    }

    private _dispatchVisibilityAnalyticsEvent(): void {
        const eventData = AnalyticsService.buildVisibilityGalleryEvent(
            'display',
            this.tagName.toLowerCase(),
            this.id ?? '',
            'display roundtrip gallery',
            'roundtrip gallery',
            this.lang ?? 'de-CH'
        );

        AnalyticsService.dispatch(this, eventData);
    }

    private _handleOpenGallery = async (event: Event): Promise<void> => {
        event.stopPropagation();

        const requestedStartIndex =
            event instanceof CustomEvent && Number.isFinite(event.detail?.index) ? event.detail.index : 0;
        const overlaySource = this._effectiveOverlayImages;
        const canReusePreloadedOverlayImages = Boolean(
            this.overlayImages?.length && this._preloadedOverlayImages.length
        );

        const sortedImages = canReusePreloadedOverlayImages
            ? overlaySource
            : sortGalleryImagesByAspectRatio(
                  await preloadAspectRatios(overlaySource, {
                      preferredSize: 'DESKTOP',
                  }),
                  {
                      sortMode: 'landscape',
                  }
              );

        this._overlayImages = buildAllGalleryRenderData(sortedImages);
        this._overlayOpen = true;
        const shouldSkipGrid =
            this._overlayImages.length > 0 && (this._overlayImages.length < 6 || this._isCompactGalleryViewport());
        const safeStartIndex = Math.min(Math.max(requestedStartIndex, 0), this._overlayImages.length - 1);
        this._currentSlideIndex = shouldSkipGrid ? safeStartIndex : 0;
        this._fullscreenAnalyticsSent = false;

        if (!shouldSkipGrid) {
            const eventData = AnalyticsService.buildVisibilityGalleryEvent(
                'open_overview',
                this.tagName.toLowerCase(),
                this.id ?? '',
                'open gallery overview',
                'roundtrip gallery interaction',
                this.lang ?? 'de-CH'
            );

            AnalyticsService.dispatch(this, eventData);
        }
        // If fewer than 6 images are available or the viewport is compact, open fullscreen view directly
        this._activeIndex = shouldSkipGrid ? safeStartIndex : null;
    };

    private _selectImage = (index: number): void => {
        this._activeIndex = index;
        this._currentSlideIndex = index;
    };

    private _backToGrid = (): void => {
        this._destroyEmbla();
        this._activeIndex = null;
        this._currentSlideIndex = 0;
        this._fullscreenAnalyticsSent = false;
    };

    private _destroyEmbla(): void {
        this._emblaInstance?.destroy();
        this._emblaInstance = null;
    }

    private _setupEmblaCarousel(startIndex: number): void {
        const viewportNode = this.renderRoot.querySelector<HTMLElement>('.embla__viewport');
        if (!viewportNode) return;

        this._destroyEmbla();
        const plugins = this._shouldUseEmblaFade() ? [Fade()] : [];
        this._emblaInstance = EmblaCarousel(viewportNode, { loop: true, startIndex }, plugins);
        let lastSelectedIndex = this._emblaInstance.selectedScrollSnap();
        this._currentSlideIndex = lastSelectedIndex;
        this._emblaInstance.on('select', () => {
            const nextIndex = this._emblaInstance?.selectedScrollSnap() ?? 0;
            if (nextIndex === lastSelectedIndex) return;

            lastSelectedIndex = nextIndex;
            this._currentSlideIndex = nextIndex;
            this._dispatchGallerySlideAnalyticsEvent();
        });

        const prevButtonNodes = this.renderRoot.querySelectorAll<HTMLButtonElement>('[data-embla-prev]');
        const nextButtonNodes = this.renderRoot.querySelectorAll<HTMLButtonElement>('[data-embla-next]');

        prevButtonNodes.forEach((button) => {
            button.onclick = () => this._emblaInstance?.scrollPrev();
        });
        nextButtonNodes.forEach((button) => {
            button.onclick = () => this._emblaInstance?.scrollNext();
        });
    }

    private _dispatchGallerySlideAnalyticsEvent(): void {
        const eventData = AnalyticsService.buildVisibilityGalleryEvent(
            'gallery_slide',
            this.tagName.toLowerCase(),
            this.id ?? '',
            'navigate gallery fullscreen view',
            'roundtrip gallery interaction',
            this.lang ?? 'de-CH'
        );

        AnalyticsService.dispatch(this, eventData);
    }

    private async _setupEmblaAfterRender(startIndex: number): Promise<void> {
        await this.updateComplete;

        if (!this._overlayOpen || this._activeIndex === null || !this._overlayImages.length) {
            return;
        }

        const viewportNode = this.renderRoot.querySelector<HTMLElement>('.embla__viewport');
        if (viewportNode) {
            this._setupEmblaCarousel(startIndex);
        }
    }

    private _renderOverlayContent(images: ImageRenderData[]): TemplateResult {
        if (!images.length) return html``;

        if (this._activeIndex !== null && images[this._activeIndex]) {
            if (!this._fullscreenAnalyticsSent) {
                const eventData = AnalyticsService.buildVisibilityGalleryEvent(
                    'open_fullscreen',
                    this.tagName.toLowerCase(),
                    this.id ?? '',
                    'open gallery fullscreen view',
                    'roundtrip gallery interaction',
                    this.lang ?? 'de-CH'
                );

                AnalyticsService.dispatch(this, eventData);
                this._fullscreenAnalyticsSent = true;
            }

            return galleryFullscreenTemplate(images, this._currentSlideIndex);
        }

        return html` ${galleryGridTemplate(images, (i) => this._selectImage(i))} `;
    }

    /**
     * Reacts to property updates and reloads localization when `lang` changes.
     * @param changedProperties Map of changed properties with previous values
     */
    override updated(changedProperties: Map<string, unknown>): void {
        if (changedProperties.has('lang')) {
            const langAttribute = this.lang ?? 'de-CH';
            void updateLocale(langAttribute);
        }

        const middlewareRelevantKeys = ['brand', 'tenant', 'lang', 'currency', 'identifier', 'travelContext'];
        const shouldRefetchImages = middlewareRelevantKeys.some((key) => changedProperties.has(key));
        if (shouldRefetchImages) {
            void this._loadGalleryFromMiddleware();
        }

        if (changedProperties.has('images') || changedProperties.has('overlayImages')) {
            void this._preloadProvidedImageRatios();
        }

        const isFullscreenMode = this._overlayOpen && this._activeIndex !== null;
        if (!isFullscreenMode) {
            this._destroyEmbla();
            return;
        }

        if (!this._overlayImages.length) {
            this._destroyEmbla();
            return;
        }

        const currentIndex = this._activeIndex;
        if (currentIndex === null) {
            this._destroyEmbla();
            return;
        }

        const shouldInitializeEmbla =
            changedProperties.has('_overlayOpen') ||
            changedProperties.has('_activeIndex') ||
            changedProperties.has('_overlayImages');

        if (!shouldInitializeEmbla) {
            return;
        }

        const safeStartIndex = Math.min(Math.max(currentIndex, 0), this._overlayImages.length - 1);
        void this._setupEmblaAfterRender(safeStartIndex);
    }

    private _emitSchemaJsonEvent(): void {
        const images = this.images?.length ? this.images : this._middlewareImages;
        if (!images.length) return;

        triggerSchemaJsonEvent(
            {
                type: 'Gallery',
                uri: setInitialUri(this.identifier),
                data: images,
            },
            this
        );
    }

    private async _preloadProvidedImageRatios(): Promise<void> {
        const teaserSource = this.images?.length ? this.images : [];
        const overlaySource = this.overlayImages?.length ? this.overlayImages : teaserSource;

        if (!teaserSource.length && !overlaySource.length) {
            this._preloadedImages = [];
            this._preloadedOverlayImages = [];
            this._isPreparingProvidedImages = false;
            return;
        }

        const runId = ++this._preloadRunId;
        this._isPreparingProvidedImages = true;

        const [preloadedTeaser, preloadedOverlay] = await Promise.all([
            preloadAspectRatios(teaserSource, {
                preferredSize: 'DESKTOP',
            }),
            preloadAspectRatios(overlaySource, {
                preferredSize: 'DESKTOP',
            }),
        ]);

        if (runId !== this._preloadRunId) return;

        this._preloadedImages = preloadedTeaser;
        this._preloadedOverlayImages = preloadedOverlay;
        this._isPreparingProvidedImages = false;
        this._emitSchemaJsonEvent();
    }

    private async _loadGalleryFromMiddleware(): Promise<void> {
        if (this.images?.length || this.overlayImages?.length) {
            return;
        }

        const requiredParams = {
            brand: this.brand,
            tenant: this.tenant,
            lang: this.lang,
            currency: this.currency,
            identifier: this.identifier,
        };

        const hasMissingRequiredParams = Object.values(requiredParams).some((value) => !value?.trim());
        if (hasMissingRequiredParams) {
            this._lastMiddlewareRequestKey = null;
            this._middlewareImages = [];
            this._middlewareResolved = true;
            return;
        }

        const requestKey = JSON.stringify({
            ...requiredParams,
            travelcontext: this.travelContext,
        });

        if (requestKey === this._lastMiddlewareRequestKey) {
            return;
        }

        this._lastMiddlewareRequestKey = requestKey;
        this._middlewareResolved = false;

        try {
            const response = await fetchRoundtripGalleryContent({
                ...requiredParams,
                travelcontext: this.travelContext,
            });

            const middlewareImages = Array.isArray(response.images) ? response.images : [];
            const preloadedMiddlewareImages = await preloadAspectRatios(middlewareImages, {
                preferredSize: 'DESKTOP',
            });
            this._middlewareImages = preloadedMiddlewareImages;
            this._middlewareResolved = true;
            this._emitSchemaJsonEvent();
        } catch {
            this._lastMiddlewareRequestKey = null;
            this._middlewareImages = [];
            this._middlewareResolved = true;
        }
    }

    /**
     * Renders the gallery component including the image teaser and overlay.
     *
     * Wrapped in a skeleton-region that reserves an aspect-ratio-based
     * placeholder while unresolved (`loading`), renders the real gallery once
     * resolved with content (`loaded`), and collapses smoothly to 0 height if
     * resolved with no images at all (`empty`) - see ContentState above.
     * @returns Template result
     */
    protected render(): TemplateResult {
        const skeletonState = this._skeletonState;

        if (skeletonState === 'loading') {
            return html`
                <div class="der-tm-roundtrip-gallery-skeleton-region loading">
                    <div class="der-tm-roundtrip-gallery-component">
                        <div class="image-gallery-wrapper image-count-5" aria-hidden="true">
                            <div class="image-gallery-left">
                                <div class="image-gallery-left-image">
                                    <div class="der-tm-gallery-skeleton-fill der-tm-gallery-skeleton-fill--big"></div>
                                    <div class="image-gallery-left-action">
                                        <div class="der-tm-gallery-skeleton-pill"></div>
                                    </div>
                                </div>
                            </div>
                            <div class="image-gallery-right">
                                ${[0, 1, 2, 3].map(
                                    () => html`
                                        <div class="image-gallery-right-image">
                                            <div class="der-tm-gallery-skeleton-fill"></div>
                                        </div>
                                    `
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            `;
        }

        const images = this._effectiveImages;
        if (!images?.length) {
            return html`<div class="der-tm-roundtrip-gallery-skeleton-region empty">${nothing}</div>`;
        }

        const overlayImages = this._overlayOpen ? this._overlayImages : [];
        const hasGrid =
            this._overlayImages.length >= 6 && (typeof window === 'undefined' || !this._isCompactGalleryViewport());
        return html`
            <div class="der-tm-roundtrip-gallery-skeleton-region loaded">
                <div class="der-tm-roundtrip-gallery-component">
                    <der-tm-roundtrip-image-gallery
                        .images=${images}
                        @open-gallery=${this._handleOpenGallery}
                    ></der-tm-roundtrip-image-gallery>
                    <der-tm-roundtrip-gallery-overlay
                        ?open=${this._overlayOpen}
                        .backEnabled=${this._activeIndex !== null && hasGrid}
                        .fullscreenMode=${this._activeIndex !== null}
                        .content=${this._renderOverlayContent(overlayImages)}
                        .ariaLabel=${msg('Image gallery', {
                            desc: 'Button label to open the image gallery',
                        })}
                        @overlay-back=${this._backToGrid}
                        @overlay-close=${() => {
                            this._destroyEmbla();
                            this._overlayOpen = false;
                            this._activeIndex = null;
                            this._currentSlideIndex = 0;
                            this._fullscreenAnalyticsSent = false;
                        }}
                        @gallery-close=${() => {
                            this._destroyEmbla();
                            this._overlayOpen = false;
                            this._activeIndex = null;
                            this._currentSlideIndex = 0;
                            this._fullscreenAnalyticsSent = false;
                        }}
                    ></der-tm-roundtrip-gallery-overlay>
                </div>
            </div>
        `;
    }
}

if (!customElements.get('der-tm-roundtrip-gallery')) {
    customElements.define('der-tm-roundtrip-gallery', RoundtripGallery);
}
